import React from 'react';
import {
  ExpansionPanel,
  ExpansionPanelContent
} from '@progress/kendo-react-layout';
import { useToggle } from 'usehooks-ts';
import { PDFExport } from '@progress/kendo-react-pdf';
import { exportImage } from '@progress/kendo-drawing';
import { Button } from '@progress/kendo-react-buttons';
import { QRCode } from '@progress/kendo-react-barcodes';
import { Reveal } from '@progress/kendo-react-animation';

import { QRTaskPDFExport } from './pdf-export';
import { GameDocumentContext } from '../../../../contexts/game-document';
import { GetResourceValue } from '../../../../utils/game-document/resources';

type ExpansionPanelContentProps = React.ComponentProps<
  typeof ExpansionPanelContent
>;

export const ExpansionContentDisabledKeys: React.FC<
  ExpansionPanelContentProps
> = ({ children, ...props }) => {
  return (
    <ExpansionPanelContent
      {...props}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.stopPropagation();
        }
      }}>
      {children}
    </ExpansionPanelContent>
  );
};

const QR_IMAGE_SIZE = 600;

const sanitizeFileName = (name: string) => name.replace(/[\\/:*?"<>|]/g, '_');

const downloadDataUri = (dataUri: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = dataUri;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type ExportItem = { id: string; title: string; qrValue: string };

export const QRTaskList: React.FC = () => {
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [showPanel, toggleShowPanel] = useToggle();

  const pdfExportRef = React.useRef<PDFExport>(null);
  const [exportItems, setExportItems] = React.useState<ExportItem[]>([]);
  const [exportFileName, setExportFileName] = React.useState('QR tasks');
  const [pendingSave, setPendingSave] = React.useState(false);

  const qrImageRef = React.useRef<QRCode>(null);
  const [imageItem, setImageItem] = React.useState<ExportItem | undefined>();

  const QRTaskList = React.useMemo(() => {
    if (!gameDocument.gameDocument) return [];
    return (gameDocument.gameDocument.assets.tasks ?? []).filter(
      (t) => t.enableQRCode
    );
  }, [gameDocument.gameDocument]);

  const exportPdf = (items: ExportItem[], fileName: string) => {
    setExportItems(items);
    setExportFileName(fileName);
    setPendingSave(true);
  };

  React.useEffect(() => {
    if (pendingSave && exportItems.length > 0) {
      pdfExportRef.current?.save();
      setPendingSave(false);
    }
  }, [pendingSave, exportItems]);

  React.useEffect(() => {
    if (!imageItem) return;

    const visual = qrImageRef.current?.exportVisual();
    if (!visual) {
      setImageItem(undefined);
      return;
    }

    exportImage(visual, {
      width: QR_IMAGE_SIZE,
      height: QR_IMAGE_SIZE
    })
      .then((dataUri) =>
        downloadDataUri(
          dataUri,
          `QR - ${sanitizeFileName(imageItem.title)}.png`
        )
      )
      .catch((err) => console.error('QR image export failed', err))
      .finally(() => setImageItem(undefined));
  }, [imageItem]);

  if (!gameDocument.gameDocument || QRTaskList.length === 0) return null;

  const allItems = QRTaskList.map((t) => ({
    id: String(t.id),
    title: GetResourceValue(gameDocument.gameDocument!, t.titleResId ?? '', ''),
    qrValue: String(t.id)
  }));

  return (
    <>
      <ExpansionPanel
        title={
          <div className={'d-flex justify-content-between'}>
            <div className={'d-flex flex-column'}>
              <span className={'fw-bold text-black'}>QR code tasks</span>
              <small className={'text-muted'} style={{ fontWeight: 'normal' }}>
                These tasks require a physical QR code to be scanned for them to
                open
              </small>
            </div>
          </div>
        }
        subtitle={
          <div className={'d-flex gap-2 align-items-center'}>
            <Button
              themeColor={'primary'}
              onClick={() =>
                exportPdf(
                  allItems,
                  `QR - All tasks ${GetResourceValue(gameDocument.gameDocument!, gameDocument.gameDocument!.overview?.titleResId ?? '', '')}`
                )
              }>
              Download all (PDF)
            </Button>
            <span>{showPanel ? 'Hide' : 'Show'}</span>
          </div>
        }
        expanded={showPanel}
        onAction={toggleShowPanel}>
        <Reveal className={'qr-reveal'}>
          {showPanel && (
            <ExpansionContentDisabledKeys>
              <div className={'d-flex flex-column'}>
                {QRTaskList.map((task, idx) => {
                  const item = allItems[idx];
                  return (
                    <div
                      key={task.id}
                      className={
                        'd-flex align-items-center justify-content-between py-2 border-bottom'
                      }>
                      <div className={'d-flex flex-column'}>
                        <span className={'fw-bold'}>{item.title}</span>
                        <small className={'text-muted'}>
                          {task.description}
                        </small>
                      </div>

                      <div className={'d-flex gap-2'}>
                        <QRCode
                          size={50}
                          value={item.qrValue}
                          errorCorrection="M"
                        />
                        <Button
                          fillMode={'outline'}
                          themeColor={'primary'}
                          onClick={() => setImageItem(item)}>
                          <div className={'d-flex align-items-center'}>
                            <span className={'material-symbols-outlined'}>
                              imagesmode
                            </span>
                          </div>
                        </Button>
                        <Button
                          fillMode={'outline'}
                          themeColor={'primary'}
                          onClick={() =>
                            exportPdf([item], `QR - ${item.title}`)
                          }>
                          <div className={'d-flex align-items-center'}>
                            <span className={'material-symbols-outlined'}>
                              picture_as_pdf
                            </span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ExpansionContentDisabledKeys>
          )}
        </Reveal>
      </ExpansionPanel>

      <QRTaskPDFExport
        {...(gameDocument.gameDocument.overview?.logoResId && {
          logoUrl: GetResourceValue(
            gameDocument.gameDocument,
            gameDocument.gameDocument.overview.logoResId,
            ''
          )
        })}
        fileName={exportFileName}
        gameTitle={GetResourceValue(
          gameDocument.gameDocument,
          gameDocument.gameDocument.overview?.titleResId ?? '',
          ''
        )}
        pdfExportRef={pdfExportRef}
        task={exportItems}
      />

      {imageItem && (
        <div
          style={{ position: 'absolute', top: '-99999px', left: '-99999px' }}>
          <QRCode
            key={imageItem.qrValue}
            ref={qrImageRef}
            size={QR_IMAGE_SIZE}
            value={imageItem.qrValue}
            errorCorrection={'M'}
            background={'#ffffff'}
            padding={24}
          />
        </div>
      )}
    </>
  );
};
